import React from "react";
import {
    Box,
    FormControl,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Select,
    Typography,
} from "@mui/material";
import {
    FlightTakeoff,
    LocationOn,
    Terrain,
    AccessTime,
} from "@mui/icons-material";

const getOperativaLabel = (aerodromo) => {
    if (aerodromo.operativa24h) {
        return "24 Hrs";
    }
    if (aerodromo.horaInicio !== undefined && aerodromo.horaFin !== undefined) {
        return `${aerodromo.horaInicio} a ${aerodromo.horaFin} Hrs`;
    }
    return null;
};

export default function SelectorAerodromo({
    aerodromos,
    onChange,
    idAerodromoSel,
    sx,
}) {
    const selectedAerodromo = aerodromos.find((a) => a.id === idAerodromoSel);

    return (
        <Box sx={{ ...sx }}>
            <Typography
                variant="subtitle2"
                sx={{
                    fontWeight: 600,
                    color: "#424242",
                    mb: 1.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                }}
            >
                <FlightTakeoff sx={{ fontSize: 18, color: "#1a8a9e" }} />
                Aeródromo
            </Typography>

            <FormControl fullWidth>
                <Select
                    value={idAerodromoSel}
                    onChange={(e) => onChange(e.target.value)}
                    renderValue={() => {
                        if (!selectedAerodromo) return "Seleccionar aeródromo";
                        const operativaLabel = getOperativaLabel(selectedAerodromo);
                        return (
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                    overflow: "hidden",
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        width: 32,
                                        height: 32,
                                        borderRadius: 1,
                                        bgcolor: "#1a8a9e",
                                        color: "white",
                                        flexShrink: 0,
                                    }}
                                >
                                    <FlightTakeoff sx={{ fontSize: 20 }} />
                                </Box>
                                <Box sx={{ overflow: "hidden" }}>
                                    <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 600, color: "#2a8fa6", lineHeight: 1.2 }}
                                        >
                                            {selectedAerodromo.nombreCorto}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{ color: "#757575" }}
                                        >
                                            ({selectedAerodromo.periodo})
                                        </Typography>
                                    </Box>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: "#757575",
                                            display: "block",
                                            lineHeight: 1.2,
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                        }}
                                    >
                                        {selectedAerodromo.ubicacion} · {selectedAerodromo.elevacion}m
                                        {operativaLabel && ` · Operativa: ${operativaLabel}`}
                                    </Typography>
                                </Box>
                            </Box>
                        );
                    }}
                    sx={{
                        bgcolor: "white",
                        borderRadius: 2,
                        height: 56,
                        "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e0e0e0",
                            borderWidth: 2,
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#80deea",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#1a8a9e",
                        },
                        "& .MuiSelect-select": {
                            py: 1.5,
                            display: "flex",
                            alignItems: "center",
                        },
                    }}
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                borderRadius: 2,
                                boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                                mt: 1,
                                "& .MuiMenuItem-root": {
                                    py: 1.5,
                                    px: 2,
                                    "&:hover": {
                                        bgcolor: "#f5f5f5",
                                    },
                                    "&.Mui-selected": {
                                        bgcolor: "#e0f7fa",
                                        "&:hover": {
                                            bgcolor: "#b2ebf2",
                                        },
                                    },
                                },
                            },
                        },
                    }}
                >
                    {aerodromos.map((aerodromo) => {
                        const operativaLabel = getOperativaLabel(aerodromo);
                        return (
                            <MenuItem key={aerodromo.id} value={aerodromo.id}>
                                <ListItemIcon
                                    sx={{
                                        minWidth: 40,
                                        color:
                                            idAerodromoSel === aerodromo.id
                                                ? "#1a8a9e"
                                                : "#757575",
                                    }}
                                >
                                    <FlightTakeoff sx={{ fontSize: 20 }} />
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                                            <span>{aerodromo.nombreCorto}</span>
                                            <Typography
                                                component="span"
                                                variant="caption"
                                                sx={{ color: "#757575" }}
                                            >
                                                ({aerodromo.periodo})
                                            </Typography>
                                        </Box>
                                    }
                                    secondary={
                                        <Box
                                            component="span"
                                            sx={{
                                                display: "flex",
                                                flexWrap: "wrap",
                                                gap: 1,
                                                mt: 0.5,
                                            }}
                                        >
                                            <Box
                                                component="span"
                                                sx={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    gap: 0.5,
                                                    fontSize: "0.7rem",
                                                    color: "#757575",
                                                }}
                                            >
                                                <LocationOn sx={{ fontSize: 12 }} />
                                                {aerodromo.ubicacion}
                                            </Box>
                                            <Box
                                                component="span"
                                                sx={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    gap: 0.5,
                                                    fontSize: "0.7rem",
                                                    color: "#757575",
                                                }}
                                            >
                                                <Terrain sx={{ fontSize: 12 }} />
                                                {aerodromo.elevacion}m
                                            </Box>
                                            {operativaLabel && (
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        gap: 0.5,
                                                        fontSize: "0.7rem",
                                                        color: "#1a8a9e",
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    <AccessTime sx={{ fontSize: 12 }} />
                                                    Operativa: {operativaLabel}
                                                </Box>
                                            )}
                                        </Box>
                                    }
                                    primaryTypographyProps={{
                                        fontWeight:
                                            idAerodromoSel === aerodromo.id ? 600 : 500,
                                        fontSize: "0.875rem",
                                        color:
                                            idAerodromoSel === aerodromo.id
                                                ? "#2a8fa6"
                                                : "#424242",
                                    }}
                                />
                            </MenuItem>
                        );
                    })}
                </Select>
            </FormControl>
        </Box>
    );
}
